import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Home, Search, Library } from "lucide-react";
import MobileNav from "./MobileNav";
import UserAvatarMenu from "./UserAvatarMenu";
import { useAuth } from "../contexts/AuthContext";
import { useSearchController } from "../hooks/useSearchController";
import { useSavedBooksCount } from "../hooks/useSavedBooks";

// Logo component matching Figma design exactly
const Logo = () => (
  <Link
    to="/"
    className="flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-[#FF6900] focus:ring-offset-2 rounded-sm"
  >
    {/* Logo Icon from Figma */}
    <img src="/images/logo-icon.svg" alt="" className="h-8 w-auto" aria-hidden="true" />
    {/* Logo Text from Figma */}
    <img src="/images/logo-text.svg" alt="chatable" className="h-[22px] w-auto dark:invert" />
  </Link>
);

const Header = () => {
  const { t } = useTranslation();
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { user, isAuthenticated, signOut } = useAuth();
  const { goToSearch } = useSearchController();
  const { data: savedBooksCount } = useSavedBooksCount(isAuthenticated);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSignOut = () => {
    signOut();
  };

  const buildRedirectState = () => ({
    redirectTo: `${location.pathname}${location.search}`,
  });

  const handleLoginClick = () => {
    navigate("/login", { state: buildRedirectState() });
  };

  const handleSignupClick = () => {
    navigate("/signup", { state: buildRedirectState() });
  };

  return (
    <header
      className={`h-[72px] bg-[var(--color-bg-surface)] border-b border-[var(--color-border-default)] sticky top-0 z-40 transition-shadow ${
        isScrolled ? "shadow-sm" : ""
      }`}
    >
      <div className="h-full max-w-[1280px] mx-auto px-8 flex items-center justify-between">
        {/* Left side: Logo + Navigation - Figma: gap-16px */}
        <div className="flex items-center gap-4">
          <Logo />

          <nav
            id="main-navigation"
            className="hidden lg:flex items-center gap-0.5"
            role="navigation"
            aria-label="Main navigation"
          >
            {/* Home Nav Item - Figma: px-12px py-8px gap-8px rounded-6px */}
            <Link
              to="/"
              className="flex items-center gap-2 px-3 py-2 rounded-[6px] bg-[var(--color-bg-surface)] hover:bg-[var(--color-bg-hover)] transition-colors"
            >
              <Home className="w-5 h-5 text-[var(--color-text-muted)] shrink-0" />
              <span className="font-semibold text-[16px] leading-6 text-[var(--color-text-secondary)]">
                Home
              </span>
            </Link>

            {/* Library Nav Item - for authenticated users */}
            {isAuthenticated && (
              <Link
                to="/library"
                className="flex items-center gap-2 px-3 py-2 rounded-[6px] bg-white hover:bg-[#F4F1EE] dark:bg-gray-900 dark:hover:bg-gray-800 transition-colors"
              >
                <Library className="w-5 h-5 text-[#BFB6AE] shrink-0" />
                <span className="font-semibold text-[16px] leading-6 text-[#69635C] dark:text-gray-300">
                  {t("navigation.library", "Library")}
                </span>
                {savedBooksCount?.count !== undefined && savedBooksCount.count > 0 && (
                  <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium bg-[#FF6900] text-white rounded-full min-w-[20px]">
                    {savedBooksCount.count > 99 ? "99+" : savedBooksCount.count}
                  </span>
                )}
              </Link>
            )}

            {/* Search Nav Item - Figma: px-12px py-8px gap-8px rounded-6px */}
            <Link
              to="/gallery"
              className="flex items-center gap-2 px-3 py-2 rounded-[6px] bg-white hover:bg-[#F4F1EE] dark:bg-gray-900 dark:hover:bg-gray-800 transition-colors"
            >
              <Search className="w-5 h-5 text-[#BFB6AE] shrink-0" />
              <span className="font-semibold text-[16px] leading-6 text-[#69635C] dark:text-gray-300">
                {t("navigation.search", "Search")}
              </span>
            </Link>
          </nav>
        </div>

        {/* Right side: Auth buttons - Figma: gap-12px */}
        <div className="flex items-center gap-3">
          {isAuthenticated ? (
            <UserAvatarMenu avatarUrl={user?.avatarUrl ?? null} onSignOut={handleSignOut} />
          ) : (
            <>
              {/* Login Button - Figma: px-16px py-10px rounded-8px border-#d4cdc6 */}
              <button
                onClick={handleLoginClick}
                className="hidden sm:inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-[var(--color-bg-surface)] border border-[var(--color-border-default)] rounded-lg hover:bg-[var(--color-bg-hover)] transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF6900] focus:ring-offset-2"
                aria-label={t("navigation.login")}
              >
                <span className="px-0.5 font-semibold text-[16px] leading-6 text-[var(--color-text-secondary)]">
                  {t("navigation.login")}
                </span>
              </button>

              {/* Sign Up Button - Figma: px-16px py-10px rounded-8px bg-#ff6900 with skeuomorphic shadow */}
              <button
                onClick={handleSignupClick}
                className="relative inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-[#FF6900] rounded-lg shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)] hover:bg-[#EC6100] transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF6900] focus:ring-offset-2 overflow-hidden"
                aria-label={t("navigation.signup")}
              >
                <span className="px-0.5 font-semibold text-[16px] leading-6 text-[#2C1409]">
                  {t("navigation.signup")}
                </span>
                {/* Skeuomorphic inner shadow */}
                <span className="absolute inset-0 pointer-events-none rounded-lg shadow-[inset_0px_0px_0px_1px_rgba(10,13,18,0.18),inset_0px_-2px_0px_0px_rgba(10,13,18,0.05)]" />
              </button>
            </>
          )}

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] rounded-md transition-colors"
            onClick={() => setIsMobileNavOpen(true)}
            aria-label="Open mobile menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>
      </div>

      <MobileNav
        open={isMobileNavOpen}
        onClose={() => setIsMobileNavOpen(false)}
        onLoginClick={handleLoginClick}
        onSignupClick={handleSignupClick}
        onSearchSubmit={value => goToSearch(value, { replace: location.pathname === "/search" })}
      />
    </header>
  );
};

export default Header;
